// ─── Geocoding: beach name → lat/lng ────────────────────────────────────────
export async function geocodeBeach(name) {
  const url = `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(name)}&count=5&language=en&format=json`
  const res = await fetch(url)
  const data = await res.json()
  return data.results || []
}

// ─── Open-Meteo Marine API ───────────────────────────────────────────────────
export async function fetchMarineForecast(lat, lng) {
  const params = new URLSearchParams({
    latitude: lat,
    longitude: lng,
    hourly: [
      'wave_height', 'wave_direction', 'wave_period',
      'wind_wave_height', 'swell_wave_height', 'swell_wave_period', 'swell_wave_direction',
      'ocean_current_velocity', 'sea_surface_temperature',
    ].join(','),
    daily: [
      'wave_height_max', 'wave_period_max', 'wind_wave_height_max',
      'swell_wave_height_max',
    ].join(','),
    wind_speed_unit: 'mph',
    timezone: 'auto',
    forecast_days: 7,
  })

  const res = await fetch(`https://marine-api.open-meteo.com/v1/marine?${params}`)
  const marine = await res.json()

  // Also fetch weather for wind + UV
  const wxParams = new URLSearchParams({
    latitude: lat,
    longitude: lng,
    hourly: ['wind_speed_10m', 'wind_direction_10m', 'uv_index', 'weather_code'].join(','),
    daily: ['sunrise', 'sunset', 'uv_index_max', 'wind_speed_10m_max', 'precipitation_probability_max'].join(','),
    wind_speed_unit: 'mph',
    timezone: 'auto',
    forecast_days: 7,
  })

  const wxRes = await fetch(`https://api.open-meteo.com/v1/forecast?${wxParams}`)
  const weather = await wxRes.json()

  return { marine, weather }
}

// ─── NOAA Tides API (US beaches) ────────────────────────────────────────────
export async function fetchNOAATides(stationId) {
  if (!stationId) return null
  const today = new Date()
  const begin = formatDate(today)
  const end = formatDate(new Date(today.getTime() + 7 * 86400000))

  const url = `https://api.tidesandcurrents.noaa.gov/api/prod/datagetter?` +
    `begin_date=${begin}&end_date=${end}&station=${stationId}` +
    `&product=predictions&datum=MLLW&time_zone=lst_ldt&interval=hilo&units=english&application=wavesync&format=json`

  try {
    const res = await fetch(url)
    const data = await res.json()
    return data.predictions || null
  } catch {
    return null
  }
}

function formatDate(d) {
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`
}

// ─── Helper: get current hour index ─────────────────────────────────────────
export function getCurrentHourIndex(times) {
  const now = new Date()
  return times.findIndex(t => new Date(t) >= now) || 0
}

// ─── Helper: surf score 1-10 ─────────────────────────────────────────────────
export function calcSurfScore({ waveHeight, wavePeriod, windSpeed }) {
  let score = 5
  // Wave height sweet spot 3-8ft
  if (waveHeight >= 1 && waveHeight < 1.5) score += 1
  if (waveHeight >= 1.5 && waveHeight < 2.5) score += 2
  if (waveHeight >= 2.5 && waveHeight < 3.5) score += 3
  if (waveHeight >= 3.5) score += 2
  // Period — longer = better
  if (wavePeriod >= 12) score += 2
  else if (wavePeriod >= 8) score += 1
  // Wind — lower = better
  if (windSpeed < 5) score += 1
  else if (windSpeed > 20) score -= 2
  else if (windSpeed > 30) score -= 4
  return Math.max(1, Math.min(10, Math.round(score)))
}

// ─── Helper: wave height in feet ─────────────────────────────────────────────
export function metersToFeet(m) {
  return m ? (m * 3.281).toFixed(1) : '—'
}

// ─── Helper: compass direction ────────────────────────────────────────────────
export function degreesToCompass(deg) {
  if (deg == null) return '—'
  const dirs = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW']
  return dirs[Math.round(deg / 22.5) % 16]
}

// ─── Helper: weather code → description ──────────────────────────────────────
export function weatherCodeToDesc(code) {
  const map = {
    0: 'Clear', 1: 'Mainly Clear', 2: 'Partly Cloudy', 3: 'Overcast',
    45: 'Foggy', 48: 'Icy Fog', 51: 'Light Drizzle', 53: 'Drizzle', 55: 'Heavy Drizzle',
    61: 'Light Rain', 63: 'Rain', 65: 'Heavy Rain',
    71: 'Light Snow', 73: 'Snow', 75: 'Heavy Snow',
    80: 'Rain Showers', 81: 'Heavy Showers', 82: 'Violent Showers',
    95: 'Thunderstorm', 96: 'Thunderstorm', 99: 'Thunderstorm'
  }
  return map[code] || 'Unknown'
}

export function weatherCodeToEmoji(code) {
  if (code === 0) return '☀️'
  if (code <= 2) return '⛅'
  if (code <= 3) return '☁️'
  if (code <= 48) return '🌫️'
  if (code <= 67) return '🌧️'
  if (code <= 77) return '❄️'
  if (code <= 82) return '🌦️'
  return '⛈️'
}
